import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NetworkIcon, PlugIcon, ServerIcon, ArrowRightIcon, WifiIcon, ZapIcon, LayersIcon, ActivityIcon, TagIcon, ShieldCheckIcon, ClipboardCheckIcon, PenToolIcon, WrenchIcon, HeadphonesIcon, ImageIcon, AwardIcon, CheckCircleIcon, HelpCircleIcon, ChevronDownIcon } from 'lucide-react';
const services = [{
  icon: NetworkIcon,
  title: 'Structured Cabling',
  description: 'Comprehensive design and installation of structured cabling systems to support your data, voice, and video networks.',
  color: 'text-blue-500',
  bg: 'bg-blue-50'
}, {
  icon: PlugIcon,
  title: 'Cat5 & Cat6 Installation',
  description: 'High-performance Ethernet cabling (Cat5e, Cat6, Cat6a) installations ensuring maximum bandwidth and minimal interference.',
  color: 'text-emerald-500',
  bg: 'bg-emerald-50'
}, {
  icon: ServerIcon,
  title: 'Network Infrastructure Setup',
  description: 'Complete server room build-outs, rack installations, patch panel terminations, and cable management solutions.',
  color: 'text-purple-500',
  bg: 'bg-purple-50'
}];
const technicalStandards = [{
  icon: ZapIcon,
  title: 'Cat5e & Cat6',
  desc: 'Standard gigabit networking for general office and data requirements.'
}, {
  icon: ActivityIcon,
  title: 'Cat6a (10Gb)',
  desc: 'Future-proof 10-gigabit speeds for high-bandwidth environments.'
}, {
  icon: LayersIcon,
  title: 'Fiber Optic',
  desc: 'High-speed backbones for multi-floor or long-distance connectivity.'
}, {
  icon: NetworkIcon,
  title: 'Coaxial',
  desc: 'Specialized cabling for video distribution and legacy systems.'
}];
const testingFeatures = [{
  icon: ShieldCheckIcon,
  title: 'Fluke Certification',
  desc: 'Every drop is tested with industry-standard Fluke equipment for performance.'
}, {
  icon: TagIcon,
  title: 'Professional Labeling',
  desc: 'Clear, standardized labeling at both the wall plate and the patch panel.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Documentation',
  desc: 'Full floor plan mapping and test results provided upon completion.'
}];
const processSteps = [{
  icon: ClipboardCheckIcon,
  title: 'Survey',
  desc: 'On-site walk-through to determine cable paths and rack locations.'
}, {
  icon: PenToolIcon,
  title: 'Design',
  desc: 'Detailed cabling plan following TIA/EIA industry standards.'
}, {
  icon: WrenchIcon,
  title: 'Install',
  desc: 'Professional pulling, termination, and rack organization.'
}, {
  icon: HeadphonesIcon,
  title: 'Certify',
  desc: 'Final testing, labeling, and delivery of certification reports.'
}];
export function EthernetCablingPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  return <div className="bg-white" data-id="element-1327">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-slate-900" data-id="element-1328">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-1329"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-1330"></div>
        <div className="absolute top-0 right-1/3 -translate-y-1/4 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-1331"></div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1332">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-1333">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1334">
              <NetworkIcon className="w-4 h-4 mr-2 text-emerald-400" data-id="element-1335" />
              Ethernet Cabling
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-1336">
              Structured
              <br className="hidden md:block" data-id="element-1337" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-1338">
                Ethernet Cabling
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-10" data-id="element-1339">
              Professional structured cabling, Cat5/Cat6 installation, and
              network infrastructure setup for reliable, high-speed
              connectivity.
            </p>
            <button onClick={() => onNavigate('home')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-1340">
              Get a Cabling Quote
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1341" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-slate-50" data-id="element-1342">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1343">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1344">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1345">
              Expert Cabling Solutions
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1346">
              A reliable network starts with proper wiring. We ensure your
              physical infrastructure is built to handle today's bandwidth
              demands and tomorrow's growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1347">
            {services.map((service, i) => {
            const Icon = service.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1348">
                  <div className={`w-14 h-14 ${service.bg} rounded-2xl flex items-center justify-center mb-6`} data-id="element-1349">
                    <Icon className={`w-7 h-7 ${service.color}`} data-id="element-1350" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1351">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-1352">
                    {service.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Technical Standards */}
      <section className="py-24 bg-white" data-id="element-1353">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1354">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1355">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1356">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-1357">
                Built to Industry Standards
              </h2>
              <p className="text-lg text-slate-600 mb-8" data-id="element-1358">
                We don't just pull wires; we build infrastructure. Our
                installations follow TIA/EIA standards to ensure your network is
                scalable, manageable, and high-performing.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1359">
                {technicalStandards.map((standard, i) => <div key={i} className="flex gap-4" data-id="element-1360">
                    <div className="flex-shrink-0 w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-1361">
                      <standard.icon className="w-5 h-5 text-emerald-600" data-id="element-1362" />
                    </div>
                    <div data-id="element-1363">
                      <h4 className="font-bold text-slate-900" data-id="element-1364">
                        {standard.title}
                      </h4>
                      <p className="text-sm text-slate-500" data-id="element-1365">{standard.desc}</p>
                    </div>
                  </div>)}
              </div>
            </motion.div>
            <div className="relative" data-id="element-1366">
              <img src="https://images.unsplash.com/photo-1558494949-ef010cbdcc51?auto=format&fit=crop&q=80&w=800" alt="Server Rack Cabling" className="rounded-3xl shadow-2xl" data-id="element-1367" />
              <div className="absolute -bottom-6 -right-6 bg-slate-900 p-8 rounded-2xl shadow-xl border border-white/10 hidden md:block" data-id="element-1368">
                <p className="text-primary font-bold text-2xl mb-1" data-id="element-1369">TIA/EIA</p>
                <p className="text-white text-sm" data-id="element-1370">Compliant Installation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Gallery / Before & After */}
      <section className="py-24 bg-slate-50" data-id="element-1371">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1372">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1373">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1374">
              From Chaos to Structured Excellence
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1375">
              We specialize in cleaning up "spaghetti wiring" and building
              clean, manageable, and professional server racks.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-1376">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm" data-id="element-1377">
              <div className="aspect-video bg-red-50 rounded-2xl mb-6 flex items-center justify-center border border-red-100 overflow-hidden relative" data-id="element-1378">
                <div className="absolute top-4 left-4 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full z-10" data-id="element-1379">
                  BEFORE
                </div>
                <img src="https://images.unsplash.com/photo-1558494949-ef010cbdcc51?auto=format&fit=crop&q=80&w=800" alt="Messy Server Rack" className="w-full h-full object-cover opacity-60 grayscale" data-id="element-1380" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-1381">
                The Problem: Spaghetti Wiring
              </h3>
              <p className="text-slate-600 text-sm" data-id="element-1382">
                Unlabeled, tangled cables lead to network downtime, overheating
                equipment, and make troubleshooting nearly impossible for IT
                staff.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm" data-id="element-1383">
              <div className="aspect-video bg-emerald-50 rounded-2xl mb-6 flex items-center justify-center border border-emerald-100 overflow-hidden relative" data-id="element-1384">
                <div className="absolute top-4 left-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full z-10" data-id="element-1385">
                  AFTER
                </div>
                <img src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&q=80&w=800" alt="Clean Server Rack" className="w-full h-full object-cover" data-id="element-1386" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-1387">
                The Solution: Structured Cabling
              </h3>
              <p className="text-slate-600 text-sm" data-id="element-1388">
                Properly routed, velcro-managed, and meticulously labeled
                cables. Easy to maintain, visually impressive, and built to
                perform.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testing & Certification */}
      <section className="py-24 bg-slate-900 text-white" data-id="element-1389">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1390">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1391">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1392">
              Testing & Certification
            </h2>
            <p className="text-slate-400" data-id="element-1393">
              We don't consider a job done until every connection is verified
              and certified for performance.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1394">
            {testingFeatures.map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white/5 border border-white/10 p-8 rounded-2xl text-center" data-id="element-1395">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1396">
                  <feature.icon className="w-8 h-8 text-emerald-400" data-id="element-1397" />
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-1398">{feature.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-1399">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Warranty & Quality Guarantee */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden border-t border-white/10" data-id="element-1400">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-[100px]" data-id="element-1401"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1402">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1403">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1404">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1405">
                Backed by Industry-Leading Warranties
              </h2>
              <p className="text-slate-400 text-lg mb-8" data-id="element-1406">
                Because we use premium materials and certified technicians, we
                can offer warranties that protect your investment for decades.
              </p>
              <div className="space-y-6" data-id="element-1407">
                <div className="flex items-start gap-4" data-id="element-1408">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center shrink-0" data-id="element-1409">
                    <AwardIcon className="w-6 h-6 text-emerald-400" data-id="element-1410" />
                  </div>
                  <div data-id="element-1411">
                    <h4 className="font-bold text-white text-lg" data-id="element-1412">
                      15-25 Year Manufacturer Warranty
                    </h4>
                    <p className="text-sm text-slate-400 mt-1" data-id="element-1413">
                      On all certified end-to-end cabling installations.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1414">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center shrink-0" data-id="element-1415">
                    <ShieldCheckIcon className="w-6 h-6 text-emerald-400" data-id="element-1416" />
                  </div>
                  <div data-id="element-1417">
                    <h4 className="font-bold text-white text-lg" data-id="element-1418">
                      Lifetime Workmanship Guarantee
                    </h4>
                    <p className="text-sm text-slate-400 mt-1" data-id="element-1419">
                      We stand behind the quality of our physical installation
                      forever.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            <div className="relative" data-id="element-1420">
              <div className="bg-white/5 backdrop-blur-md border border-white/10 p-10 rounded-3xl" data-id="element-1421">
                <h3 className="text-2xl font-bold mb-6" data-id="element-1422">
                  Why Certification Matters
                </h3>
                <ul className="space-y-4" data-id="element-1423">
                  {['Guarantees cables meet strict TIA/EIA performance standards', 'Identifies hidden faults like near-end crosstalk (NEXT)', 'Ensures your network can handle advertised speeds (1Gb, 10Gb)', 'Required to activate manufacturer warranties'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1424">
                      <CheckCircleIcon className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" data-id="element-1425" />
                      <span className="text-slate-300 text-sm" data-id="element-1426">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 bg-white" data-id="element-1427">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1428">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1429">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1430">
              Our Cabling Process
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1431">
              A structured approach for a structured network.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-1432">
            {processSteps.map((step, i) => <div key={i} className="text-center" data-id="element-1433">
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1434">
                  <step.icon className="w-8 h-8 text-primary" data-id="element-1435" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-1436">
                  {step.title}
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-1437">{step.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Cabling FAQ */}
      <section className="py-24 bg-slate-50" data-id="element-1438">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1439">
          <div className="text-center mb-16" data-id="element-1440">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 flex items-center justify-center gap-3" data-id="element-1441">
              <HelpCircleIcon className="w-8 h-8 text-emerald-600" data-id="element-1442" />
              Cabling FAQ
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1443">
              Common questions about network infrastructure.
            </p>
          </div>
          <div className="space-y-4" data-id="element-1444">
            {[{
            q: 'Should I choose Cat5e or Cat6?',
            a: 'While Cat5e supports gigabit speeds, we highly recommend Cat6 or Cat6a for new installations. The cost difference in materials is minimal, but Cat6 provides better performance, less interference, and future-proofs your office for 10-gigabit speeds.'
          }, {
            q: 'Do you remove old cabling?',
            a: 'Yes. In fact, local fire codes often require the removal of abandoned cabling in drop ceilings. We can safely remove and dispose of legacy wiring during your new installation.'
          }, {
            q: 'Do you work after hours or on weekends?',
            a: 'Yes. We understand that pulling cables in an active office can be disruptive. We offer flexible scheduling, including nights and weekends, to ensure zero impact on your daily operations.'
          }, {
            q: 'How do you test the cables?',
            a: "We use enterprise-grade Fluke Networks certification testers. These devices don't just check if the wire is connected; they push data through the cable to measure resistance, crosstalk, and signal loss to guarantee performance."
          }, {
            q: 'What is plenum cable and do I need it?',
            a: 'Plenum-rated cable has a special fire-retardant jacket. It is required by building codes whenever cables are run through air circulation spaces (like drop ceilings or raised floors) to prevent the spread of toxic smoke in a fire.'
          }].map((faq, i) => <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden bg-white" data-id="element-1445">
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors" data-id="element-1446">
                  <span className="font-bold text-slate-900" data-id="element-1447">{faq.q}</span>
                  <motion.div animate={{
                rotate: activeFaq === i ? 180 : 0
              }} data-id="element-1448">
                    <ChevronDownIcon className="w-5 h-5 text-slate-400" data-id="element-1449" />
                  </motion.div>
                </button>
                <AnimatePresence data-id="element-1450">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-1451">
                      <div className="p-6 pt-0 text-slate-600 border-t border-slate-100" data-id="element-1452">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary py-20 relative overflow-hidden" data-id="element-1453">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-1454"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center" data-id="element-1455">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1456">
            Building a new office or upgrading your network?
          </h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-1457">
            Our certified technicians ensure your cabling is installed cleanly,
            tested thoroughly, and certified for performance.
          </p>
          <button onClick={() => onNavigate('contact')} className="bg-white text-primary px-10 py-4 rounded-full font-bold text-lg hover:bg-slate-50 transition-all shadow-xl inline-flex items-center" data-id="element-1458">
            Schedule a Site Survey
            <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1459" />
          </button>
        </div>
      </section>
    </div>;
}